// Handles interactive controls inside .detail-spotlight sections (IN/CM toggle, accordion toggles for callouts and the disclaimer, variant-driven size highlight).
class DetailSpotlight {
	// Size aliases mirrored from detail-spotlight-sizing-chart.liquid.
	static #SIZE_ALIAS_PAIRS = [
		['s', 'small'],
		['m', 'medium'],
		['l', 'large'],
		['xl', 'x-large'],
		['xl', 'extra-large'],
		['xl', 'xlarge'],
		['xxl', 'xx-large'],
		['xxl', '2xl'],
	];

	// Page-scoped singleton — theme-editor reloads re-execute this script and would otherwise stack listeners on every reload.
	static #instance = null;

	constructor() {
		if (DetailSpotlight.#instance) {
			DetailSpotlight.#instance.#init();
			return DetailSpotlight.#instance;
		}
		DetailSpotlight.#instance = this;
		this.#init();
		this.#subscribeToProductEvents();
		document.addEventListener('shopify:section:load', (event) => {
			this.#init(event.target);
		});
	}

	#init(root) {
		const scope = root || document;
		scope.querySelectorAll('.detail-spotlight__sizing-toggle').forEach((toggle) => this.#bindUnitToggle(toggle));
		scope
			.querySelectorAll('.detail-spotlight [data-accordion-toggle]')
			.forEach((button) => this.#bindAccordionToggle(button));
	}

	#bindUnitToggle(toggle) {
		if (toggle.dataset.detailSpotlightBound) {
			return;
		}
		toggle.dataset.detailSpotlightBound = 'true';

		toggle.addEventListener('click', (event) => {
			const unitButton = event.target.closest('.detail-spotlight__sizing-unit');
			if (!unitButton) {
				return;
			}
			const unit = unitButton.getAttribute('data-unit');
			if (!unit) {
				return;
			}
			this.#setActiveUnit(toggle, unit);
		});
	}

	#setActiveUnit(toggle, unit) {
		const sizingContainer = toggle.closest('.detail-spotlight__sizing');
		if (!sizingContainer) {
			return;
		}

		const table = sizingContainer.querySelector('.detail-spotlight__sizing-table');
		if (!table) {
			return;
		}

		toggle.querySelectorAll('.detail-spotlight__sizing-unit').forEach((unitButton) => {
			const isActive = unitButton.getAttribute('data-unit') === unit;
			unitButton.classList.toggle('detail-spotlight__sizing-unit--active', isActive);
			unitButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});

		table.setAttribute('data-active-unit', unit);

		const dataAttribute = `data-${unit}`;
		table.querySelectorAll('[data-in][data-cm]').forEach((cell) => {
			const value = cell.getAttribute(dataAttribute);
			if (value !== null) {
				cell.textContent = value;
			}
		});
	}

	#bindAccordionToggle(button) {
		if (button.dataset.detailSpotlightAccordionBound) {
			return;
		}
		button.dataset.detailSpotlightAccordionBound = 'true';

		button.addEventListener('click', () => {
			const panelId = button.getAttribute('aria-controls');
			const panel = panelId ? document.getElementById(panelId) : null;
			if (!panel) {
				console.error(`DetailSpotlight: accordion toggle controls a missing panel ("${panelId}").`);
				return;
			}

			const isExpanding = panel.dataset.expanded !== 'true';
			panel.dataset.expanded = isExpanding ? 'true' : 'false';
			// Collapsed content is only visually hidden; inert keeps its links out of the tab order.
			panel.inert = !isExpanding;
			button.setAttribute('aria-expanded', isExpanding ? 'true' : 'false');
			this.#syncToggleLabels(button);
		});
	}

	// Labels without both data-text-more and data-text-less keep their text.
	#syncToggleLabels(button) {
		const isExpanded = button.getAttribute('aria-expanded') === 'true';
		button.querySelectorAll('[data-text-more][data-text-less]').forEach((label) => {
			label.textContent = isExpanded ? label.dataset.textLess : label.dataset.textMore;
		});
	}

	// Section isn't re-rendered on variant change — re-apply the highlight client-side.
	#updateHighlightedSizeRow() {
		const tables = document.querySelectorAll('.detail-spotlight__sizing-table');
		if (tables.length === 0) {
			return;
		}
		const selectedSize = this.#getSelectedSizeFromVariantPicker();
		if (!selectedSize) {
			return;
		}

		tables.forEach((table) => {
			table.querySelectorAll('.detail-spotlight__sizing-row').forEach((row) => {
				const sizeCell = row.querySelector('.detail-spotlight__sizing-cell--size');
				if (!sizeCell) {
					return;
				}
				const matches = DetailSpotlight.#sizesMatch(sizeCell.textContent, selectedSize);
				row.classList.toggle('detail-spotlight__sizing-row--highlighted', matches);
			});
		});
	}

	#getSelectedSizeFromVariantPicker() {
		const fieldsets = document.querySelectorAll('.variant-picker__options');
		for (const fieldset of fieldsets) {
			const optionNameEl = fieldset.querySelector('.variant-picker__option-name');
			if (!optionNameEl) {
				continue;
			}
			if (DetailSpotlight.#normalizeSize(optionNameEl.textContent) !== 'size') {
				continue;
			}

			const selectedValueEl = fieldset.querySelector('.variant-picker__selected-value');
			if (selectedValueEl?.textContent.trim()) {
				return selectedValueEl.textContent.trim();
			}
			const optionSelect = fieldset.querySelector('select');
			if (optionSelect?.value) {
				return optionSelect.value;
			}
			const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
			if (checkedRadio?.value) {
				return checkedRadio.value;
			}
		}
		return null;
	}

	static #normalizeSize(value) {
		return (value == null ? '' : String(value)).toLowerCase().trim();
	}

	static #sizesMatch(sizeA, sizeB) {
		const normalizedSizeA = DetailSpotlight.#normalizeSize(sizeA);
		const normalizedSizeB = DetailSpotlight.#normalizeSize(sizeB);
		if (!normalizedSizeA || !normalizedSizeB) {
			return false;
		}
		if (normalizedSizeA === normalizedSizeB) {
			return true;
		}
		for (const [aliasA, aliasB] of DetailSpotlight.#SIZE_ALIAS_PAIRS) {
			const matchesForward = normalizedSizeA === aliasA && normalizedSizeB === aliasB;
			const matchesReverse = normalizedSizeA === aliasB && normalizedSizeB === aliasA;
			if (matchesForward || matchesReverse) {
				return true;
			}
		}
		return false;
	}

	#subscribeToProductEvents() {
		if (!window.UFT?.EventManager) {
			return;
		}
		const events = window.UFT.EventManager.eventStrings;
		if (!events?.PRODUCT_VARIANT_CHANGE) {
			return;
		}
		window.UFT.EventManager.on(events.PRODUCT_VARIANT_CHANGE, () => {
			this.#updateHighlightedSizeRow();
		});
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new DetailSpotlight());
} else {
	new DetailSpotlight();
}
